import React from 'react';
import {
  Panel,
  type PanelImperativeHandle,
  type PanelSize,
} from 'react-resizable-panels';

import { DRAWER_WIDTH } from 'src/constants';

const MIN_WIDTH_IN_PIXELS = 288;
const DEFAULT_WIDTH_IN_PIXELS = 396;
const DESKTOP_MAX_WIDTH_PERCENTAGE = 55;
const MOBILE_MAX_WIDTH_PERCENTAGE = 100;
/**
 * Calculates responsive size constraints for the right panel based on viewport width.
 *
 * Mobile (< 600px): Returns 100% width
 * Desktop: Converts fixed pixel widths to percentages of available space
 *   - Min: 288px → percentage of (viewport width - 240px sidebar)
 *   - Default: 396px → percentage of (viewport width - 240px sidebar)
 *   - Max: 55% of available space
 *
 * @param viewPortWidth - Current viewport width in pixels
 * @returns Panel size configuration object with minSize, maxSize, and defaultSize in percentages
 */
export const getRightPanelSizeProps = (
  viewPortWidth: number,
  isMobile: boolean,
) => {
  if (isMobile) {
    return { minSize: MOBILE_MAX_WIDTH_PERCENTAGE };
  }

  // if viewPortWidth is not available, return default sizes
  if (!viewPortWidth) {
    return { minSize: 20, maxSize: 55, defaultSize: 30 };
  }

  // On narrow desktops/tablets the fixed pixel widths can exceed the max
  // percentage; clamp both so we never hand the panel library a size above
  // maxSize (which pins the handle at its ceiling with no room to grow).
  const minWidthInPercentage = Math.min(
    (MIN_WIDTH_IN_PIXELS / (viewPortWidth - DRAWER_WIDTH)) * 100,
    DESKTOP_MAX_WIDTH_PERCENTAGE,
  );
  const defaultWidthInPercentage = Math.min(
    (DEFAULT_WIDTH_IN_PIXELS / (viewPortWidth - DRAWER_WIDTH)) * 100,
    DESKTOP_MAX_WIDTH_PERCENTAGE,
  );
  return {
    minSize: minWidthInPercentage,
    maxSize: DESKTOP_MAX_WIDTH_PERCENTAGE,
    defaultSize: defaultWidthInPercentage,
  };
};

export type ResizablePanelProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
  collapsible?: boolean;
  /** All size props are percentages of the group (0..100), as in the v2 API. */
  collapsedSize?: number;
  minSize?: number;
  maxSize?: number;
  defaultSize?: number;
  onResize?: (sizePercent: number, prevSizePercent?: number) => void;
  onCollapse?: () => void;
  onExpand?: () => void;
};

// v4 interprets bare numbers as pixels; our contract stays percent-based.
const toPercent = (value: number | undefined) =>
  value === undefined ? undefined : `${value}%`;

export const ResizablePanel = React.forwardRef<
  PanelImperativeHandle,
  ResizablePanelProps
>(
  (
    {
      children,
      className,
      id,
      collapsible,
      collapsedSize,
      minSize,
      maxSize,
      defaultSize,
      onResize,
      onCollapse,
      onExpand,
    },
    ref,
  ) => {
    const collapsedPercent = collapsedSize ?? 0;
    const handleResize = React.useCallback(
      (
        size: PanelSize,
        _id: string | number | undefined,
        prevSize: PanelSize | undefined,
      ) => {
        onResize?.(size.asPercentage, prevSize?.asPercentage);
        if (!collapsible) return;
        // v4 dropped onCollapse/onExpand; recreate the v2 semantics, including
        // firing onCollapse when a persisted layout restores collapsed on mount.
        const isNowCollapsed = size.asPercentage === collapsedPercent;
        const wasCollapsed =
          prevSize === undefined
            ? undefined
            : prevSize.asPercentage === collapsedPercent;
        if (isNowCollapsed && wasCollapsed !== true) {
          onCollapse?.();
        } else if (!isNowCollapsed && wasCollapsed === true) {
          onExpand?.();
        }
      },
      [onResize, onCollapse, onExpand, collapsible, collapsedPercent],
    );
    return (
      <Panel
        panelRef={ref}
        id={id}
        collapsible={collapsible}
        collapsedSize={toPercent(collapsedSize)}
        minSize={toPercent(minSize)}
        maxSize={toPercent(maxSize)}
        defaultSize={toPercent(defaultSize)}
        onResize={handleResize}
        className={className}
        // v2 panels clipped their content; v4's inner div defaults to overflow
        // auto, so restore hidden here (inline style wins over the default).
        style={{ overflow: 'hidden' }}
      >
        {children}
      </Panel>
    );
  },
);
ResizablePanel.displayName = 'ResizablePanel';
